import { useCallback, useEffect, useState } from 'react';

export type CopyOutcome = 'copied' | 'failed';

export interface CopyStatus<Key> {
  readonly key: Key;
  readonly outcome: CopyOutcome;
}

/** Copies text and briefly remembers which item was copied, or failed to copy, for feedback. */
export function useClipboard<Key>(
  feedbackMs = 1500,
  writeText?: (text: string) => void | Promise<void>,
) {
  const [status, setStatus] = useState<CopyStatus<Key> | null>(null);

  useEffect(() => {
    if (!status) return;
    const timer = setTimeout(() => {
      setStatus(null);
    }, feedbackMs);
    return () => {
      clearTimeout(timer);
    };
  }, [status, feedbackMs]);

  const copy = useCallback(
    async (key: Key, text: string): Promise<CopyOutcome> => {
      let outcome: CopyOutcome = 'copied';
      try {
        if (writeText) await writeText(text);
        else await navigator.clipboard.writeText(text);
      } catch {
        outcome = 'failed';
      }
      setStatus({ key, outcome });
      return outcome;
    },
    [writeText],
  );

  return { status, copy };
}
